import React from "react";
import { Calendar, Clock } from "lucide-react";
import { formatAudAmount } from "../../../utils/audCurrency";

const buildAvatarDataUri = (name = "Teacher") => {
  const safeName = String(name || "Teacher").trim() || "Teacher";
  const initial = safeName.charAt(0).toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f97316" />
          <stop offset="100%" stop-color="#ef4444" />
        </linearGradient>
      </defs>
      <rect width="128" height="128" rx="24" fill="url(#bg)" />
      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="48" font-weight="700" fill="white">
        ${initial}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const PaymentModal = ({
  tutor,
  formattedDate,
  selectedTime,
  duration,
  onClose,
  onConfirm,
  isSubmitting = false,
  confirmLabel = "Confirm & Pay",
  variant = "default",
}) => {
  const isParentVariant = variant === "parent";
  const avatarFallback = buildAvatarDataUri(tutor?.name);

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[999]">
      <div
        className={`rounded-2xl p-8 w-[550px] shadow-xl ${
          isParentVariant ? "border border-[#ddd3c6] bg-[#fffaf4]" : "bg-white"
        }`}
      >
        <h2 className="text-center text-xl font-semibold mb-8">
          Session Details
        </h2>

        <div
          className={`flex gap-4 items-center p-6 rounded-xl ${
            isParentVariant ? "bg-[#fcf7ef]" : "bg-[#F9F9FB]"
          }`}
        >
          <img
            src={tutor.image}
            alt="tutor"
            className="w-16 h-16 rounded-xl object-cover"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = avatarFallback;
            }}
          />

          <div className="flex flex-col gap-1">
            <p className="font-semibold text-gray-900">{tutor.name}</p>

            <div className="flex gap-4 text-sm text-gray-700">
              <span className="flex items-center gap-1">
                <Calendar size={16} /> {formattedDate}
              </span>

              <span className="flex items-center gap-1">
                <Clock size={16} /> {selectedTime} ({duration || "60 minutes"})
              </span>
            </div>
          </div>
        </div>

        <div
          className={`p-6 border rounded-xl mt-6 ${
            isParentVariant
              ? "border-[#eadfcd] bg-[#fffdf9]"
              : "border-gray-200 bg-white"
          }`}
        >
          <h3 className="font-semibold text-gray-900 mb-4">Payment Summary</h3>

          <div className="flex justify-between text-gray-700 text-sm mb-2">
            <span>Session Fee</span>
            <span>{formatAudAmount(tutor.price)}</span>
          </div>

          <div className="border-t my-3"></div>

          <div className="flex justify-between text-green-600 font-semibold text-sm">
            <span>Total Amount</span>
            <span>{formatAudAmount(tutor.price)}</span>
          </div>
        </div>

        <div className="flex gap-4 mt-8">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-1/2 py-3 border border-gray-400 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className={`w-1/2 py-3 text-white rounded-xl shadow-md font-medium ${
              isParentVariant
                ? "bg-[#08059B] hover:bg-[#01044F]"
                : "bg-[#0736D6] hover:bg-[#062FC0]"
            }`}
          >
            {isSubmitting ? confirmLabel : "Confirm & Pay"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
